<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { Plus, Send, Trash2, X } from 'lucide-vue-next'
import {
  formatBrl,
  formatBrlInput,
  formatDecimalInput,
  formatIntegerInput,
  parseBrl,
  parseDecimalInput,
  parseIntegerInput,
  type InventoryItemSnapshot,
  type QuoteLineKind,
  type Repair
} from '@ck/domain'
import { ApiError, inventoryApi, repairsApi, type CreateRepairInput, type InventoryStockItem, type QuoteLineInput } from '@ck/api-client'
import { CkAlert, CkButton, CkField, CkModal } from '@ck/ui'

const props = defineProps<{ workOrderId: string; stageId: string }>()
const queryClient = useQueryClient()

const repairsQuery = useQuery({
  queryKey: ['repairs', props.workOrderId],
  queryFn: () => repairsApi.listByWorkOrder(props.workOrderId)
})
const repairs = computed(() => (repairsQuery.data.value ?? []).filter((repair) => repair.stageExecutionId === props.stageId))

const statusLabel: Record<Repair['status'], string> = {
  draft: 'Rascunho',
  pendingApproval: 'Aguardando cliente',
  approved: 'Aprovado',
  rejected: 'Recusado'
}

interface LineForm {
  kind: QuoteLineKind
  description: string
  quantity: string
  unitPrice: string
  inventoryItemId: string | null
  inventorySnapshot: InventoryItemSnapshot | null
  quantityHasFraction?: boolean
}
interface LineErrors {
  description?: string
  quantity?: string
  unitPrice?: string
  inventory?: string
}

const editingId = ref<string | null>(null)
const editingVersion = ref<number | null>(null)
const editingStatus = ref<Repair['status'] | null>(null)
const showForm = ref(false)
const saving = ref(false)
const form = reactive<{ title: string; description: string; lines: LineForm[] }>({ title: '', description: '', lines: [] })
const formError = ref<string | null>(null)
const conflictMessage = ref<string | null>(null)
const actionError = ref<string | null>(null)
const lineErrors = ref<Record<number, LineErrors>>({})
const pendingDelete = ref<Repair | null>(null)

function emptyLine(): LineForm {
  return { kind: 'part', description: '', quantity: '1', unitPrice: 'R$ 0,00', inventoryItemId: null, inventorySnapshot: null }
}
function displayQuantity(kind: QuoteLineKind, quantity: number): string {
  return kind === 'part' ? String(quantity) : formatDecimalInput(String(quantity).replace('.', ','))
}
function openNew() {
  editingId.value = null
  editingVersion.value = null
  editingStatus.value = null
  form.title = ''
  form.description = ''
  form.lines = [emptyLine()]
  formError.value = null
  conflictMessage.value = null
  actionError.value = null
  lineErrors.value = {}
  saveMutation.reset()
  showForm.value = true
}
function openEdit(repair: Repair) {
  editingId.value = repair.id
  editingVersion.value = repair.version
  editingStatus.value = repair.status
  form.title = repair.title
  form.description = repair.description ?? ''
  form.lines = repair.quoteLines.map((line) => ({
    kind: line.kind,
    description: line.description,
    quantity: displayQuantity(line.kind, line.quantity),
    unitPrice: formatBrl(line.unitPrice),
    inventoryItemId: line.inventoryItemId ?? null,
    inventorySnapshot: line.inventorySnapshot ?? null
  }))
  if (form.lines.length === 0) form.lines = [emptyLine()]
  formError.value = null
  conflictMessage.value = null
  actionError.value = null
  lineErrors.value = {}
  saveMutation.reset()
  showForm.value = true
}
function closeForm() {
  showForm.value = false
  editingId.value = null
  editingVersion.value = null
  editingStatus.value = null
}
function addLine() { form.lines.push(emptyLine()) }
function removeLine(index: number) {
  form.lines.splice(index, 1)
  const nextErrors: Record<number, LineErrors> = {}
  form.lines.forEach((_, lineIndex) => { if (lineErrors.value[lineIndex < index ? lineIndex : lineIndex + 1]) nextErrors[lineIndex] = lineErrors.value[lineIndex < index ? lineIndex : lineIndex + 1]! })
  lineErrors.value = nextErrors
}
function onKindChange(index: number) {
  const line = form.lines[index]
  if (!line) return
  if (line.kind === 'labor') {
    line.inventoryItemId = null
    line.inventorySnapshot = null
  }
  line.quantityHasFraction = false
}
function setQuantity(index: number, value: string) {
  const line = form.lines[index]
  if (!line) return
  line.quantityHasFraction = line.kind === 'part' && /[.,]/.test(value)
  line.quantity = line.kind === 'part' ? formatIntegerInput(value) : formatDecimalInput(value)
}
function setUnitPrice(index: number, value: string) {
  const line = form.lines[index]
  if (line) line.unitPrice = formatBrlInput(value)
}
function lineSubtotal(line: LineForm) {
  const quantity = line.kind === 'part' ? parseIntegerInput(line.quantity) : parseDecimalInput(line.quantity)
  const unitPrice = parseBrl(line.unitPrice)
  return (quantity ?? 0) * (unitPrice ?? 0)
}
const formTotal = computed(() => form.lines.reduce((sum, line) => sum + lineSubtotal(line), 0))

const inventoryModalOpen = ref(false)
const inventorySearch = ref('')
const inventoryLineIndex = ref<number | null>(null)
const inventoryQuery = useQuery(computed(() => ({
  queryKey: ['inventory-items', inventorySearch.value],
  queryFn: () => inventoryApi.listAvailable(inventorySearch.value),
  enabled: inventoryModalOpen.value,
  staleTime: 30_000
})))
const inventoryItems = computed(() => {
  const data = inventoryQuery.data.value
  const items = Array.isArray(data) ? data : (data?.items ?? [])
  return items.filter((item) => item.active !== false && (item.availableQuantity === undefined || item.availableQuantity > 0))
})
function openInventory(index: number) {
  inventoryLineIndex.value = index
  inventorySearch.value = ''
  inventoryModalOpen.value = true
}
function closeInventory() {
  inventoryModalOpen.value = false
  inventoryLineIndex.value = null
}
function snapshotOf(item: InventoryStockItem, unitPrice: number | null): InventoryItemSnapshot {
  return { id: item.id, code: item.sku, description: item.description ?? item.name, unitPrice: unitPrice ?? 0 }
}
function selectInventoryItem(item: InventoryStockItem) {
  const index = inventoryLineIndex.value
  const line = index === null ? undefined : form.lines[index]
  if (!line || line.kind !== 'part') return
  line.inventoryItemId = item.id
  line.inventorySnapshot = snapshotOf(item, parseBrl(line.unitPrice))
  line.description = item.description ?? item.name
  closeInventory()
}
function removeInventorySelection(index: number) {
  const line = form.lines[index]
  if (!line) return
  line.inventoryItemId = null
  line.inventorySnapshot = null
}
function inventoryLabel(line: LineForm): string {
  return line.inventorySnapshot ? `Estoque · ${line.inventorySnapshot.code}` : 'Selecionar peça do estoque'
}

function setLineError(index: number, field: keyof LineErrors, message: string) {
  lineErrors.value[index] = { ...lineErrors.value[index], [field]: message }
}
function toPayload(): CreateRepairInput | null {
  formError.value = null
  lineErrors.value = {}
  if (form.title.trim().length < 2) { formError.value = 'Informe um título para o reparo.'; return null }
  const quoteLines: QuoteLineInput[] = []
  for (const [index, line] of form.lines.entries()) {
    const quantity = line.kind === 'part' ? parseIntegerInput(line.quantity) : parseDecimalInput(line.quantity)
    const unitPrice = parseBrl(line.unitPrice)
    if (!line.description.trim()) setLineError(index, 'description', 'Descreva a linha do orçamento.')
    if (line.kind === 'part' && line.quantityHasFraction) setLineError(index, 'quantity', 'Quantidade de peça deve ser um número inteiro.')
    else if (quantity === null || quantity <= 0) setLineError(index, 'quantity', line.kind === 'part' ? 'Informe uma quantidade inteira maior que zero.' : 'Informe horas decimais maiores que zero.')
    if (unitPrice === null || unitPrice < 0) setLineError(index, 'unitPrice', 'Informe um valor em reais válido.')
    if (line.kind === 'part' && ((line.inventoryItemId && !line.inventorySnapshot) || (!line.inventoryItemId && line.inventorySnapshot))) {
      setLineError(index, 'inventory', 'Selecione novamente a peça do estoque.')
    }
    if (lineErrors.value[index]) continue
    const quoteLine: QuoteLineInput = {
      kind: line.kind,
      description: line.description.trim(),
      quantity: quantity!,
      unitPrice: unitPrice!
    }
    if (line.kind === 'part' && line.inventoryItemId && line.inventorySnapshot) {
      quoteLine.inventoryItemId = line.inventoryItemId
      quoteLine.inventorySnapshot = line.inventorySnapshot
    }
    quoteLines.push(quoteLine)
  }
  if (Object.keys(lineErrors.value).length > 0) {
    formError.value = 'Revise as linhas destacadas antes de salvar.'
    return null
  }
  if (quoteLines.length === 0) { formError.value = 'Adicione ao menos uma linha de orçamento.'; return null }
  return { title: form.title.trim(), description: form.description.trim() || null, quoteLines }
}

async function invalidate() {
  await queryClient.invalidateQueries({ queryKey: ['repairs', props.workOrderId] })
  await queryClient.invalidateQueries({ queryKey: ['work-order', props.workOrderId] })
}

const saveMutation = useMutation({
  mutationFn: (command: { id: string | null; expectedVersion: number | null; payload: CreateRepairInput }) => {
    if (command.id !== null && command.expectedVersion !== null) {
      return repairsApi.update(command.id, { ...command.payload, expectedVersion: command.expectedVersion })
    }
    return repairsApi.create(props.workOrderId, props.stageId, command.payload)
  }
})
const submitMutation = useMutation({
  mutationFn: (id: string) => repairsApi.submit(id),
  onSuccess: async () => { actionError.value = null; await invalidate() },
  onError: (error: unknown) => { actionError.value = error instanceof ApiError ? error.message : 'Não foi possível enviar o reparo ao cliente.' }
})
const deleteMutation = useMutation({
  mutationFn: (id: string) => repairsApi.remove(id),
  onSuccess: async () => { pendingDelete.value = null; actionError.value = null; await invalidate() },
  onError: (error: unknown) => { actionError.value = error instanceof ApiError ? error.message : 'Não foi possível arquivar o reparo.' }
})

function requestDelete(repair: Repair) {
  actionError.value = null
  deleteMutation.reset()
  pendingDelete.value = repair
}

function confirmDelete() {
  if (pendingDelete.value) deleteMutation.mutate(pendingDelete.value.id)
}

async function save() {
  if (saving.value) return
  const payload = toPayload()
  if (!payload) return
  if (editingId.value !== null && editingVersion.value === null) {
    formError.value = 'Reabra o orçamento antes de salvar.'
    return
  }

  saving.value = true
  conflictMessage.value = null
  try {
    await saveMutation.mutateAsync({ id: editingId.value, expectedVersion: editingVersion.value, payload })
    await invalidate()
    closeForm()
  } catch (error) {
    if (error instanceof ApiError && error.problem.status === 409) {
      await invalidate()
      closeForm()
      conflictMessage.value = 'Este orçamento foi alterado em outra sessão. Os dados foram recarregados; abra novamente para revisar.'
    } else {
      formError.value = 'Não foi possível salvar o reparo.'
    }
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <section class="repairs">
    <div class="head">
      <h2>Reparos e orçamentos</h2>
      <CkButton v-if="!showForm" variant="secondary" class="add" @click="openNew"><Plus :size="16" aria-hidden="true" />Novo reparo</CkButton>
    </div>

    <p v-if="repairsQuery.isPending.value" class="muted">Carregando reparos…</p>
    <CkAlert v-else-if="repairsQuery.isError.value" tone="error">Não foi possível carregar os reparos.</CkAlert>
    <p v-else-if="repairs.length === 0 && !showForm" class="muted">Nenhum reparo nesta etapa. Crie um para enviar o orçamento ao cliente.</p>
    <CkAlert v-if="conflictMessage" tone="error">{{ conflictMessage }}</CkAlert>
    <CkAlert v-if="actionError" tone="error">{{ actionError }}</CkAlert>

    <ul v-if="repairs.length" class="list">
      <li v-for="repair in repairs" :key="repair.id" class="repair" :class="`is-${repair.status}`">
        <div class="repair-head">
          <strong>{{ repair.title }}</strong>
          <span class="badge" :class="`badge-${repair.status}`">{{ statusLabel[repair.status] }}</span>
        </div>
        <p v-if="repair.description" class="repair-desc">{{ repair.description }}</p>
        <ul class="lines">
          <li v-for="line in repair.quoteLines" :key="line.id">
            <span>{{ line.kind === 'labor' ? 'Mão de obra' : 'Peça' }} · {{ line.description }}<small v-if="line.inventorySnapshot"> · {{ line.inventorySnapshot.code }}</small></span>
            <span class="mono">{{ line.quantity }} × {{ formatBrl(line.unitPrice) }}</span>
          </li>
        </ul>
        <div class="repair-foot">
          <strong class="mono">{{ formatBrl(repair.total) }}</strong>
          <div v-if="repair.status === 'draft' || repair.status === 'pendingApproval'" class="actions">
            <CkButton type="button" variant="ghost" @click="openEdit(repair)">Editar</CkButton>
            <CkButton type="button" variant="ghost" class="danger" :disabled="deleteMutation.isPending.value" :aria-label="`Arquivar reparo ${repair.title}`" @click="requestDelete(repair)"><Trash2 :size="14" aria-hidden="true" /></CkButton>
            <CkButton v-if="repair.status === 'draft'" type="button" class="primary" :disabled="submitMutation.isPending.value" @click="submitMutation.mutate(repair.id)"><Send :size="14" aria-hidden="true" />Enviar ao cliente</CkButton>
          </div>
        </div>
        <p v-if="repair.status === 'rejected' && repair.customerDecisionNote" class="decision">Motivo da recusa: {{ repair.customerDecisionNote }}</p>
      </li>
    </ul>

    <form v-if="showForm" class="form" :aria-busy="saving" @submit.prevent="save">
      <div class="form-head"><strong>{{ editingId ? 'Editar reparo' : 'Novo reparo' }}</strong><CkButton type="button" variant="ghost" class="icon" aria-label="Fechar" @click="closeForm"><X :size="16" aria-hidden="true" /></CkButton></div>
      <CkField v-model="form.title" label="Título" placeholder="Ex.: Troca do coxim do motor" :maxlength="120" :disabled="saving" />
      <CkField v-model="form.description" label="Descrição (opcional)" placeholder="Detalhes para o cliente" :maxlength="500" :disabled="saving" />
      <CkAlert v-if="editingStatus === 'pendingApproval'" tone="info">Ao salvar, este reparo volta para rascunho e deverá ser enviado novamente ao cliente.</CkAlert>

      <div class="lines-editor">
        <div v-for="(line, index) in form.lines" :key="index" class="line-row">
          <label class="line-kind"><span>Tipo</span><select v-model="line.kind" :disabled="saving" :aria-label="`Tipo da linha ${index + 1}`" @change="onKindChange(index)">
            <option value="part">Peça</option>
            <option value="labor">Mão de obra</option>
          </select></label>
          <CkField
            v-model="line.description"
            class="line-field line-desc"
            :label="`Descrição da linha ${index + 1}`"
            :placeholder="line.kind === 'labor' ? 'Serviço' : 'Peça'"
            :maxlength="160"
            :error="lineErrors[index]?.description"
            :disabled="saving"
          />
          <CkField
            class="line-field line-num"
            :model-value="line.quantity"
            :label="line.kind === 'labor' ? 'Horas' : 'Quantidade'"
            type="text"
            :inputmode="line.kind === 'labor' ? 'decimal' : 'numeric'"
            :maxlength="line.kind === 'labor' ? 9 : 6"
            :placeholder="line.kind === 'labor' ? '0,00' : '1'"
            :error="lineErrors[index]?.quantity"
            :disabled="saving"
            @update:model-value="setQuantity(index, $event)"
          />
          <CkField
            class="line-field line-price"
            :model-value="line.unitPrice"
            label="Valor unitário"
            type="text"
            inputmode="decimal"
            :maxlength="20"
            placeholder="R$ 0,00"
            :error="lineErrors[index]?.unitPrice"
            :disabled="saving"
            @update:model-value="setUnitPrice(index, $event)"
          />
          <CkButton variant="ghost" class="icon line-remove" aria-label="Remover linha" :disabled="form.lines.length === 1 || saving" @click="removeLine(index)"><X :size="14" aria-hidden="true" /></CkButton>
          <div v-if="line.kind === 'part'" class="line-inventory">
            <CkButton type="button" variant="secondary" class="inventory-button" :disabled="saving" @click="openInventory(index)">{{ inventoryLabel(line) }}</CkButton>
            <CkButton v-if="line.inventoryItemId" type="button" variant="ghost" class="inventory-remove" :disabled="saving" @click="removeInventorySelection(index)">Remover seleção</CkButton>
            <span v-if="line.inventorySnapshot" class="inventory-snapshot">Snapshot: {{ line.inventorySnapshot.description }} · {{ formatBrl(line.inventorySnapshot.unitPrice) }}</span>
            <span v-if="lineErrors[index]?.inventory" class="line-error">{{ lineErrors[index]?.inventory }}</span>
          </div>
        </div>
        <CkButton type="button" variant="ghost" class="add-line" :disabled="saving" @click="addLine"><Plus :size="14" aria-hidden="true" />Adicionar linha</CkButton>
      </div>

      <div class="form-total"><span>Total do orçamento</span><strong class="mono">{{ formatBrl(formTotal) }}</strong></div>
      <CkAlert v-if="formError" tone="error">{{ formError }}</CkAlert>
      <div class="form-actions">
        <CkButton type="button" variant="ghost" :disabled="saving" @click="closeForm">Cancelar</CkButton>
        <CkButton type="submit" :disabled="saving">{{ saving ? 'Salvando…' : 'Salvar rascunho' }}</CkButton>
      </div>
    </form>
  </section>

  <CkModal v-if="inventoryModalOpen" title="Selecionar peça do estoque" @close="closeInventory">
    <CkField v-model="inventorySearch" label="Buscar peça" placeholder="Código ou descrição" inputmode="search" :maxlength="80" />
    <CkAlert v-if="inventoryQuery.isError.value" tone="error">Não foi possível carregar o estoque. Você ainda pode informar a peça manualmente.</CkAlert>
    <p v-else-if="inventoryQuery.isPending.value" class="muted">Carregando peças disponíveis…</p>
    <p v-else-if="inventoryItems.length === 0" class="muted">Nenhuma peça disponível para esta busca.</p>
    <ul v-else class="inventory-list">
      <li v-for="item in inventoryItems" :key="item.id">
        <CkButton variant="ghost" class="inventory-option" @click="selectInventoryItem(item)">
          <span><strong>{{ item.description ?? item.name }}</strong><small>{{ item.sku }}<template v-if="item.availableQuantity !== undefined"> · {{ item.availableQuantity }} disponível(is)</template></small></span>
          <span class="inventory-price-hint">Valor no orçamento</span>
        </CkButton>
      </li>
    </ul>
  </CkModal>

  <CkModal v-if="pendingDelete" title="Arquivar reparo" @close="pendingDelete = null">
    <p class="modal-copy">O reparo será retirado da etapa ativa, mas o orçamento e o histórico continuarão preservados para auditoria.</p>
    <p class="original-repair"><strong>{{ pendingDelete.title }}</strong><span>{{ statusLabel[pendingDelete.status] }}</span></p>
    <CkAlert v-if="deleteMutation.isError.value" tone="error">{{ actionError || 'Não foi possível arquivar o reparo.' }}</CkAlert>
    <div class="form-actions">
      <CkButton type="button" variant="ghost" :disabled="deleteMutation.isPending.value" @click="pendingDelete = null">Cancelar</CkButton>
      <CkButton type="button" variant="secondary" :disabled="deleteMutation.isPending.value" @click="confirmDelete">{{ deleteMutation.isPending.value ? 'Arquivando…' : 'Confirmar arquivamento' }}</CkButton>
    </div>
  </CkModal>
</template>

<style scoped>
.repairs{margin-top:24px;padding-top:20px;border-top:1px solid var(--ck-cream-300)}
.head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:10px}
.head h2{margin:0;font-size:12px;letter-spacing:var(--ls-widest);text-transform:uppercase;color:var(--ck-maroon-700)}
.add{display:inline-flex;align-items:center;gap:5px;padding:8px 12px;font-size:12px}
.muted{color:var(--ck-text-muted);font-size:13px;margin:6px 0}
.mono{font-family:var(--font-mono);font-variant-numeric:tabular-nums}
.list{list-style:none;margin:0;padding:0;display:grid;gap:10px}
.repair{padding:14px;border:1px solid var(--ck-cream-300);border-left:4px solid var(--ck-cream-400);border-radius:var(--radius-lg);background:var(--ck-cream-50)}
.repair.is-approved{border-left-color:var(--ck-ok-500)}
.repair.is-rejected{border-left-color:var(--ck-fail-500)}
.repair.is-pendingApproval{border-left-color:var(--ck-warn-500)}
.repair-head{display:flex;justify-content:space-between;align-items:center;gap:8px}
.badge{font-size:11px;font-weight:700;padding:3px 8px;border-radius:var(--radius-full);background:var(--ck-cream-200);color:var(--ck-text-muted)}
.badge-approved{background:var(--ck-ok-bg);color:var(--ck-ok-text)}
.badge-rejected{background:var(--ck-fail-bg);color:var(--ck-fail-text)}
.badge-pendingApproval{background:var(--ck-warn-bg);color:var(--ck-warn-text)}
.repair-desc{margin:6px 0;font-size:13px;color:var(--ck-text-muted)}
.lines{list-style:none;margin:10px 0;padding:0;display:grid;gap:6px}
.lines li{display:flex;justify-content:space-between;gap:10px;font-size:12.5px}
.lines small{color:var(--ck-text-muted)}
.repair-foot{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-top:10px}
.actions{display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end}
.actions .ck-button{min-height:36px;padding:6px 10px;font-size:12px}
.actions .primary{display:inline-flex;align-items:center;gap:4px}
.actions .danger{color:var(--ck-fail-text)}
.decision{margin:8px 0 0;font-size:12.5px;color:var(--ck-fail-text)}
.form{margin-top:14px;padding:16px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-lg);background:var(--ck-cream-50)}
.form-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:10px}
.icon{display:inline-flex;align-items:center;justify-content:center;width:44px;min-width:44px;height:44px;padding:0}
.lines-editor{display:grid;gap:10px;margin:8px 0 12px}
.line-row{display:grid;grid-template-columns:112px minmax(160px,1fr) 100px 132px 44px;gap:8px;align-items:start;padding:12px;border:1px solid var(--ck-cream-200);border-radius:var(--radius-md);background:var(--ck-cream-100)}
.line-kind{display:block;font-size:12px;font-weight:600;color:var(--ck-text-muted)}
.line-kind span{display:block;margin-bottom:4px}
.line-kind select{width:100%;min-height:44px;padding:8px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-md);background:var(--ck-cream-50);color:var(--ck-text)}
.line-field{margin-bottom:0}
.line-field :deep(input){min-height:44px;padding:10px 9px}
.line-inventory{grid-column:2/-1;display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:-2px}
.inventory-button,.inventory-remove{min-height:36px;padding:7px 10px;font-size:12px}
.inventory-button{display:inline-flex;align-items:center}
.inventory-remove{color:var(--ck-maroon-700)}
.inventory-snapshot{font-size:11px;color:var(--ck-text-muted)}
.line-error{flex-basis:100%;font-size:12px;color:var(--ck-fail-text)}
.line-remove{margin-top:20px}
.add-line{display:inline-flex;align-items:center;gap:5px;padding:8px 10px;color:var(--ck-maroon-700);font-size:12px}
.form-total{display:flex;justify-content:space-between;align-items:center;margin:8px 0 12px;font-size:14px}
.form-actions{display:flex;justify-content:flex-end;gap:8px}
.modal-copy{margin:0 0 14px;color:var(--ck-text-muted);font-size:13px;line-height:1.45}
.original-repair{display:flex;flex-direction:column;gap:3px;margin:0 0 14px;padding:10px 12px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-md);background:var(--ck-cream-100)}
.original-repair span{color:var(--ck-text-muted);font-size:12px}
.inventory-list{display:grid;gap:6px;list-style:none;margin:12px 0 0;padding:0}
.inventory-option{display:flex;align-items:center;justify-content:space-between;gap:16px;width:100%;min-height:56px;padding:10px 12px;text-align:left}
.inventory-option span{display:flex;flex-direction:column;gap:3px;min-width:0}
.inventory-option small{color:var(--ck-text-muted);font-size:11px}
.inventory-option .mono{flex-shrink:0;color:var(--ck-maroon-700);font-size:12px}

@media (max-width:720px) {
  .repair-foot{align-items:flex-start;flex-direction:column}
  .actions{justify-content:flex-start}
  .line-row{grid-template-columns:1fr 1fr}
  .line-kind,.line-desc{grid-column:span 2}
  .line-num,.line-price{grid-column:span 1}
  .line-remove{grid-column:2;grid-row:3;margin-top:20px;justify-self:end}
  .line-inventory{grid-column:1/-1}
}

@media (max-width:375px) {
  .head{align-items:flex-start;flex-direction:column}
  .add{width:100%;justify-content:center}
  .line-row{grid-template-columns:1fr}
  .line-kind,.line-desc,.line-num,.line-price,.line-remove,.line-inventory{grid-column:1}
  .line-remove{grid-row:auto;margin-top:0;justify-self:start}
  .inventory-button{width:100%;justify-content:center}
}
</style>
